/**
 * Template Generator
 * Programmatically generates industry templates at scale
 */

import { IndustryTemplate, IndustrySmartPrompts, IndustryQuestion, IndustryFinancialDefaults, IndustryChallenge } from '../types/industryTemplates';
import { SectionIdEnum } from '../types/businessPlan';

export type TemplateTier = 'premium' | 'standard' | 'quick';
export type RevenueModelType = 'subscription' | 'transaction' | 'product-sales' | 'service-hourly' | 'project-based' | 'commission' | 'advertising' | 'freemium';

export interface IndustryDataInput {
  id: string;
  name: string;
  category: 'technology' | 'retail' | 'food-beverage' | 'healthcare' | 'professional-services' | 'manufacturing' | 'real-estate' | 'education' | 'finance' | 'hospitality' | 'construction' | 'agriculture' | 'other';
  description: string;
  icon: string;
  popularity: number;
  tier: TemplateTier;
  
  // Financial characteristics
  revenueModel: RevenueModelType;
  avgRevenue: { min: number; avg: number; max: number };
  profitMargin: { min: number; avg: number; max: number };
  startupCosts: { low: number; medium: number; high: number };
  
  // Key metrics
  keyMetrics: string[];
  
  // Common expense categories
  majorExpenses: string[];
  
  // Industry-specific keywords
  keywords: string[];
}

/**
 * Generate a complete industry template from input data
 */
export function generateTemplate(data: IndustryDataInput): IndustryTemplate {
  return {
    id: data.id,
    name: data.name,
    category: data.category,
    description: data.description,
    icon: data.icon,
    popularity: data.popularity,
    isActive: true,
    
    sectionTemplates: data.tier === 'premium' ? {} : generateSectionTemplates(data),
    smartPrompts: generateSmartPrompts(data),
    financialDefaults: generateFinancialDefaults(data),
    keyMetrics: data.keyMetrics,
    commonChallenges: generateCommonChallenges(data)
  };
}

/**
 * Generate section templates based on industry
 */
function generateSectionTemplates(data: IndustryDataInput): Record<string, any> {
  if (data.tier === 'quick') {
    return {}; // Quick tier has no pre-filled sections
  }
  
  // Standard tier gets executive summary template
  return {
    [SectionIdEnum.EXECUTIVE_SUMMARY]: {
      sectionId: SectionIdEnum.EXECUTIVE_SUMMARY,
      templateContent: `# Executive Summary

## Business Overview
[Your ${data.name} business name] is a ${data.category} business focused on [specific niche/market].

## Problem & Solution
**Problem**: [Describe the market problem or customer need]

**Solution**: We provide ${data.keywords.slice(0, 3).join(', ')} to help customers [achieve specific outcome].

## Market Opportunity
The ${data.name} market is estimated at $${data.avgRevenue.avg.toLocaleString()} annually, with ${data.profitMargin.avg}% average profit margins.

## Business Model
We operate on a ${data.revenueModel} model, generating revenue through [specific revenue sources].

## Financial Highlights
- Startup Investment: $${data.startupCosts.medium.toLocaleString()}
- Year 1 Revenue Target: $${data.avgRevenue.min.toLocaleString()}
- Projected Profit Margin: ${data.profitMargin.avg}%

## Team
[Describe your team and relevant experience in ${data.category}]
`,
      placeholders: {
        'business name': `Your ${data.name} business name`,
        'niche/market': 'Specific target market',
        'outcome': 'Key customer benefit'
      }
    }
  };
}

/**
 * Generate smart prompts for the industry
 */
function generateSmartPrompts(data: IndustryDataInput): IndustrySmartPrompts {
  const questionCount = data.tier === 'quick' ? 3 : data.tier === 'standard' ? 7 : 12;
  
  const baseQuestions: IndustryQuestion[] = [
    {
      id: `${data.id}-q1`,
      question: `Who is your target customer for your ${data.name} business?`,
      type: 'textarea',
      helpText: `Consider demographics, psychographics, and specific needs in the ${data.category} sector.`,
      required: true,
      appliesTo: [SectionIdEnum.OVERVIEW, SectionIdEnum.MARKET_RESEARCH],
      options: [
        `Small businesses in ${data.category}`,
        `Consumers aged 25-45`,
        `Enterprise clients`,
        `Local community members`
      ]
    },
    {
      id: `${data.id}-q2`,
      question: `What makes your ${data.name} business different from competitors?`,
      type: 'textarea',
      helpText: `Identify your unique value proposition and competitive advantages.`,
      required: true,
      appliesTo: [SectionIdEnum.EXECUTIVE_SUMMARY, SectionIdEnum.SWOT_ANALYSIS],
      options: [
        `Lower prices`,
        `Better customer service`,
        `Specialized expertise`,
        `Innovative technology`
      ]
    },
    {
      id: `${data.id}-q3`,
      question: `How will you acquire customers for your ${data.name} business?`,
      type: 'textarea',
      helpText: `Describe your customer acquisition strategy.`,
      required: true,
      appliesTo: [SectionIdEnum.SALES_MARKETING],
      options: [
        `Social media marketing`,
        `Search engine optimization`,
        `Referral programs`,
        `Paid advertising`
      ]
    }
  ];
  
  // Add more questions for standard and premium tiers
  if (questionCount > 3) {
    baseQuestions.push(
      {
        id: `${data.id}-q4`,
        question: `What are your revenue streams?`,
        type: 'textarea',
        helpText: `List the ways you'll generate income.`,
        required: false,
        appliesTo: [SectionIdEnum.BUSINESS_MODELS, SectionIdEnum.FINANCIALS],
        options: getRevenueStreamSuggestions(data.revenueModel)
      },
      {
        id: `${data.id}-q5`,
        question: `What are your major operational expenses?`,
        type: 'multiselect',
        helpText: `Identify your largest cost categories.`,
        required: false,
        appliesTo: [SectionIdEnum.OPERATIONS, SectionIdEnum.FINANCIALS],
        options: data.majorExpenses
      },
      {
        id: `${data.id}-q6`,
        question: `What experience do you have in ${data.category}?`,
        type: 'textarea',
        helpText: `Describe relevant skills and background.`,
        required: false,
        appliesTo: [SectionIdEnum.OVERVIEW]
      },
      {
        id: `${data.id}-q7`,
        question: `How much capital do you need to start?`,
        type: 'select',
        helpText: `Estimate your startup costs.`,
        required: false,
        appliesTo: [SectionIdEnum.FINANCIALS],
        options: [
          `Under $10,000 (bootstrap)`,
          `$10,000 - $50,000 (small investment)`,
          `$50,000 - $250,000 (significant capital)`,
          `Over $250,000 (major funding needed)`
        ]
      }
    );
  }
  
  return {
    industryQuestions: baseQuestions.slice(0, questionCount),
    editingHints: {
      [SectionIdEnum.EXECUTIVE_SUMMARY]: [
        `Focus on the specific problem you solve in ${data.category}`,
        `Quantify your market opportunity with specific numbers`,
        `Highlight what makes you unique compared to competitors`
      ],
      [SectionIdEnum.MARKET_RESEARCH]: [
        `Research current trends in ${data.category}`,
        `Identify your ideal customer profile`,
        `Analyze top 3-5 direct competitors`
      ],
      [SectionIdEnum.SALES_MARKETING]: [
        `Define your ${data.revenueModel} pricing strategy`,
        `Choose 2-3 primary marketing channels`,
        `Set measurable customer acquisition goals`
      ]
    }
  };
}

/**
 * Generate financial defaults for the industry
 */
function generateFinancialDefaults(data: IndustryDataInput): IndustryFinancialDefaults {
  return {
    typicalRevenueStreams: [
      {
        name: 'Primary Revenue',
        category: data.revenueModel === 'subscription' ? 'subscription' : data.revenueModel === 'product-sales' ? 'product' : 'service',
        avgUnitPrice: Math.round(data.avgRevenue.min / 12 / 10),
        avgUnitsPerMonth: 10,
        growthRate: 50
      }
    ],
    typicalExpenses: data.majorExpenses.map((expense, index) => ({
      name: expense,
      category: index === 0 ? 'fixed' as const : 'variable' as const,
      avgAmount: Math.round(data.avgRevenue.min * (1 - data.profitMargin.avg / 100) / data.majorExpenses.length / 12),
      percentage: Math.round((100 - data.profitMargin.avg) / data.majorExpenses.length)
    })),
    benchmarks: {
      grossMargin: { 
        min: data.profitMargin.min + 10, 
        avg: data.profitMargin.avg + 10, 
        max: data.profitMargin.max + 10 
      },
      netMargin: { 
        min: data.profitMargin.min, 
        avg: data.profitMargin.avg, 
        max: data.profitMargin.max 
      },
      revenueGrowth: { min: 20, avg: 50, max: 100 },
      breakEvenMonths: { 
        min: Math.max(6, estimateBreakEven(data) - 6), 
        avg: estimateBreakEven(data), 
        max: estimateBreakEven(data) + 6 
      }
    },
    typicalStartupCosts: data.startupCosts
  };
}

/**
 * Generate common challenges for the industry
 */
function generateCommonChallenges(data: IndustryDataInput): IndustryChallenge[] {
  const challenges: IndustryChallenge[] = [
    {
      challenge: `High competition in ${data.category}`,
      solution: `Differentiate through ${data.keywords[0]}, excellent customer service, and niche specialization.`,
      resources: ['Industry associations', 'Market research reports', 'Competitive analysis tools']
    },
    {
      challenge: `Customer acquisition costs`,
      solution: `Focus on organic growth, referrals, and high-value ${data.revenueModel} strategies.`,
      resources: ['Digital marketing guides', 'CRM systems', 'Analytics tools']
    }
  ];
  
  // Add industry-specific challenges
  if (data.category === 'technology') {
    challenges.push({
      challenge: 'Rapid technology changes',
      solution: 'Invest in continuous learning, agile development, and staying ahead of trends.',
      resources: ['Tech blogs', 'Developer communities', 'Innovation labs']
    });
  } else if (data.category === 'retail') {
    challenges.push({
      challenge: 'Inventory management',
      solution: 'Implement inventory tracking systems and optimize stock levels based on demand.',
      resources: ['Inventory software', 'Demand forecasting tools', 'Supplier relationships']
    });
  } else if (data.category === 'professional-services') {
    challenges.push({
      challenge: 'Scaling service delivery',
      solution: 'Systematize processes, hire skilled team members, and leverage technology.',
      resources: ['Project management tools', 'Automation software', 'Training programs']
    });
  }
  
  return challenges;
}

/**
 * Helper: Get revenue stream suggestions based on model
 */
function getRevenueStreamSuggestions(model: RevenueModelType): string[] {
  const suggestions: Record<RevenueModelType, string[]> = {
    'subscription': ['Monthly subscriptions', 'Annual subscriptions', 'Tiered pricing plans', 'Add-on services'],
    'transaction': ['Per-transaction fees', 'Processing fees', 'Service charges', 'Premium features'],
    'product-sales': ['Product sales', 'Wholesale', 'Retail markup', 'Bundled packages'],
    'service-hourly': ['Hourly consulting', 'Retainer agreements', 'Package deals', 'Emergency services'],
    'project-based': ['Fixed-price projects', 'Milestone payments', 'Value-based pricing', 'Change orders'],
    'commission': ['Sales commissions', 'Referral fees', 'Affiliate income', 'Partnership revenue'],
    'advertising': ['Display ads', 'Sponsored content', 'Affiliate marketing', 'Premium listings'],
    'freemium': ['Free tier', 'Premium upgrades', 'Feature unlocks', 'Enterprise plans']
  };
  
  return suggestions[model] || ['Primary revenue', 'Secondary revenue', 'Additional services'];
}

/**
 * Helper: Estimate break-even month
 */
function estimateBreakEven(data: IndustryDataInput): number {
  const monthlyRevenue = data.avgRevenue.min / 12;
  const monthlyExpenses = monthlyRevenue * (1 - data.profitMargin.avg / 100);
  const startupCosts = data.startupCosts.medium;
  
  // Simple break-even calculation
  if (monthlyRevenue <= monthlyExpenses) return 24; // Default to 24 months if unprofitable
  
  const monthlyProfit = monthlyRevenue - monthlyExpenses;
  return Math.ceil(startupCosts / monthlyProfit);
}

/**
 * Batch generate templates from array of industry data
 */
export function generateBatchTemplates(industries: IndustryDataInput[]): IndustryTemplate[] {
  return industries.map(generateTemplate);
}

/**
 * Validate generated template
 */
export function validateTemplate(template: IndustryTemplate): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!template.id) errors.push('Missing template ID');
  if (!template.name) errors.push('Missing template name');
  if (!template.category) errors.push('Missing category');
  if (!template.description) errors.push('Missing description');
  if (!template.icon) errors.push('Missing icon');
  if (template.keyMetrics.length === 0) errors.push('No key metrics defined');
  if (template.commonChallenges.length === 0) errors.push('No common challenges defined');
  
  return {
    valid: errors.length === 0,
    errors
  };
}
